// ──────────────────────────────────────────────────────────────────────
// Fretless Primitives
// Reusable React components for the patterns established in the
// Gig Brief Desktop card. Use these instead of recreating styled divs.
//
//   import { Kicker, StatTile, GhostBtn, UrgencyPill,
//            Avatar, Toggle, Card, Dot } from "@/design-system/primitives";
//
// All components consume tokens from ./tokens.js — change a hex there,
// and every widget updates automatically.
// ──────────────────────────────────────────────────────────────────────

import React from "react";
import { color as c, font, type as t, space, radius, shadow, border, motion, avatarGradients } from "./tokens";

// ───── Kicker ─────────────────────────────────────────────────
// THE signature element. Section labels, eyebrow text. Always mono caps.

export function Kicker({ children, size = 13, color = c.textDim, as: As = "span", style }) {
  return (
    <As
      style={{
        fontFamily: font.mono,
        fontSize: size,
        fontWeight: 600,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
    </As>
  );
}

// ───── Dot ────────────────────────────────────────────────────
// 4px separator dot for inline metadata rows (NEXT GIG · 3 DAYS · FRI 23 MAY).

export function Dot({ color = c.textMuted, opacity = 0.55, size = 4 }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: radius.pill,
        background: color,
        opacity,
        flexShrink: 0,
      }}
    />
  );
}

// ───── Card ───────────────────────────────────────────────────
// Primary card surface. Use for any standalone widget on the dashboard.

export function Card({ children, maxWidth = 1180, style, ...rest }) {
  return (
    <div
      style={{
        width: "100%",
        maxWidth,
        margin: "0 auto",
        background: c.cardBg,
        border: border.card,
        borderRadius: radius.xxl,
        overflow: "hidden",
        boxShadow: shadow.cardLg,
        fontFamily: font.body,
        color: c.text,
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

// ───── Band ───────────────────────────────────────────────────
// One horizontal section inside a Card. Cards stack Bands separated by hairlines.
// Use `last` on the bottom-most Band to drop the divider.

export function Band({ children, padding = "22px 32px", last, style, ...rest }) {
  return (
    <div
      style={{
        padding,
        borderBottom: last ? "none" : border.hairline,
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

// ───── StatTile ───────────────────────────────────────────────
// Big numeric stat with kicker label above. Used in grid strips.
// `last` drops the right divider — useful when arranged in `repeat(3, 1fr)`.

export function StatTile({ label, value, valueColor = c.text, last, style }) {
  return (
    <div
      style={{
        padding: "26px 28px 30px",
        minWidth: 0,
        borderRight: last ? "none" : border.hairline,
        ...style,
      }}
    >
      <Kicker size={13}>{label}</Kicker>
      <div
        style={{
          marginTop: 14,
          ...t.statBig,
          color: valueColor,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </div>
    </div>
  );
}

// Convenience: StatTile row inside a Card.
export function StatStrip({ children, columns = 3, style }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        background: c.appBg,
        borderBottom: border.hairline,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ───── GhostBtn ───────────────────────────────────────────────
// Bordered button or solid brand-fill primary. Used for inline actions.
// Pass `icon` as a component reference (lucide-react), NOT a JSX element.

export function GhostBtn({ icon: Icon, label, onClick, primary, type: btnType = "button", style, ...rest }) {
  return (
    <button
      type={btnType}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        padding: "9px 13px",
        background: primary ? c.orange : "transparent",
        color: primary ? c.onOrange : c.textDim,
        border: primary ? border.brand : border.card,
        borderRadius: radius.md + 1,             // 9px — matches button family
        fontFamily: font.body,
        fontSize: 13,
        fontWeight: primary ? 600 : 500,
        cursor: "pointer",
        whiteSpace: "nowrap",
        flexShrink: 0,
        ...style,
      }}
      {...rest}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {label}
    </button>
  );
}

// ───── UrgencyPill ────────────────────────────────────────────
// Time-based status pill. Today (red, pulse), Tomorrow (amber),
// ≤7 days (orange), else nothing.

export function UrgencyPill({ daysUntil, isToday: today, isTomorrow: tomorrow }) {
  let label, bg, fg, pulse = false;
  if (today)             { label = "Today";          bg = c.red;    fg = c.onRed;    pulse = true; }
  else if (tomorrow)     { label = "Tomorrow";       bg = c.amber;  fg = c.onAmber; }
  else if (daysUntil <= 7 && daysUntil > 0) { label = `${daysUntil} days`; bg = c.orange; fg = c.onOrange; }
  else return null;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        padding: "5px 11px 5px 9px",
        background: bg,
        color: fg,
        borderRadius: radius.pill,
        fontFamily: font.mono,
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.13em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: radius.pill,
          background: fg,
          opacity: 0.85,
          animation: pulse ? "flPulse 1.4s ease-in-out infinite" : "none",
        }}
      />
      {label}
      <style>{`
        @keyframes flPulse {
          0%, 100% { opacity: 0.85; }
          50%      { opacity: 0.2; }
        }
      `}</style>
    </span>
  );
}

// ───── Avatar ─────────────────────────────────────────────────
// Gradient-filled initials circle. Pass `name` and an `idx` to pick a gradient.

export function Avatar({ name, idx = 0, size = 34, ring = c.cardBg2, style }) {
  const g = avatarGradients[idx % avatarGradients.length];
  const initials = (name || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: radius.pill,
        background: `linear-gradient(135deg, ${g.from}, ${g.to})`,
        border: `2px solid ${ring}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size <= 30 ? 10 : 12,
        fontWeight: 700,
        color: "#fff",
        letterSpacing: "0.02em",
        flexShrink: 0,
        ...style,
      }}
    >
      {initials}
    </div>
  );
}

// ───── Toggle ─────────────────────────────────────────────────
// Brand-orange iOS-style switch.

export function Toggle({ on, onChange, ariaLabel }) {
  return (
    <button
      onClick={() => onChange(!on)}
      aria-pressed={on}
      aria-label={ariaLabel}
      style={{
        width: 38,
        height: 22,
        borderRadius: radius.pill,
        background: on ? c.orange : c.toggleOff,
        border: "none",
        padding: 0,
        cursor: "pointer",
        position: "relative",
        transition: `background ${motion.base}`,
        flexShrink: 0,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 2,
          left: on ? 18 : 2,
          width: 18,
          height: 18,
          borderRadius: radius.pill,
          background: "#fff",
          transition: `left ${motion.base}`,
          boxShadow: shadow.switch,
        }}
      />
    </button>
  );
}

// ───── IconTile ───────────────────────────────────────────────
// 48×48 squircle holding a brand-tinted icon. Used as venue/section markers.

export function IconTile({ icon: Icon, size = 48, tint = c.orange, style }) {
  return (
    <div
      style={{
        flexShrink: 0,
        width: size,
        height: size,
        borderRadius: radius.lg,
        background: c.orangeSoft,
        border: border.brandSoft,
        color: tint,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...style,
      }}
    >
      <Icon className="h-5 w-5" />
    </div>
  );
}

// ───── SegmentedToggle ────────────────────────────────────────
// 2-3 option switcher. Pass `options` as [{id, icon, label}], `value` and `onChange`.

export function SegmentedToggle({ options, value, onChange }) {
  return (
    <div
      style={{
        display: "inline-flex",
        background: c.segBg,
        border: border.inner,
        borderRadius: radius.md + 2,            // 10px
        padding: 3,
        flexShrink: 0,
      }}
    >
      {options.map((o) => {
        const Icon = o.icon;
        const on = value === o.id;
        return (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 12px",
              background: on ? c.segActive : "transparent",
              color: on ? c.text : c.textMuted,
              border: "none",
              borderRadius: 7,
              cursor: "pointer",
              fontFamily: font.body,
              fontSize: 12,
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {Icon && <Icon className="h-3 w-3" />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ───── Hero ──────────────────────────────────────────────────
// Big centered display title. Use ONCE per card.

export function Hero({ children, padding = "40px 32px 36px", style }) {
  return (
    <div
      style={{
        padding,
        textAlign: "center",
        borderBottom: border.hairline,
        ...style,
      }}
    >
      <h1
        style={{
          margin: 0,
          ...t.hero,
          color: c.text,
          textWrap: "balance",
        }}
      >
        {children}
      </h1>
    </div>
  );
}

// ───── WidgetHeader ──────────────────────────────────────────
// Every dashboard widget's top band. Loud kicker title on the left, free-form
// action slots on the right. `leading` is the optional pre-title slot
// (typically a drag handle); `actions` is the post-title slot (View all,
// hide button, etc.). Keep widget headers consistent by using THIS rather
// than building band+kicker each time.
//
//   <WidgetHeader title="Upcoming gigs"
//                 leading={<DragHandle />}
//                 actions={<GhostBtn ... />} />

export function WidgetHeader({
  title,
  leading,
  actions,
  padding = `${space.lg}px ${space.xl}px`,
  style,
}) {
  return (
    <div
      style={{
        padding,
        // Pin the content row to a constant depth so every widget header lines
        // up regardless of which action controls it carries — a "View all"
        // GhostBtn (~40px) would otherwise make that header taller than one
        // with only a 32px icon button, leaving the bottom hairlines misaligned
        // across side-by-side widgets. content-box keeps this independent of the
        // padding prop (which differs desktop vs mobile).
        boxSizing: "content-box",
        minHeight: space.xxxl,
        borderBottom: border.hairline,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: space.md,
        ...style,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: space.sm, minWidth: 0 }}>
        {leading}
        <Kicker size={16} color={c.text}>{title}</Kicker>
      </div>
      {actions && (
        <div style={{ display: "flex", alignItems: "center", gap: space.sm, flexShrink: 0 }}>
          {actions}
        </div>
      )}
    </div>
  );
}

// ───── ListRow ───────────────────────────────────────────────
// Two-line list-item recipe used across the app (Upcoming Gigs, Social
// Schedule, Contacts…). Primary identity on top in titleMd/text, secondary
// context underneath in listSecondary/textDim. Optional `leading` slot for
// a date stack, avatar, or icon; optional `trailing` slot for a time, fee,
// or chevron.
//
//   <ListRow
//     leading={<DateBlock date={gig.date} />}
//     primary={gig.artist_name}
//     secondary={`${gig.venue_name} · ${gig.city}`}
//     trailing={<TimeBadge time={gig.start_time} />}
//     onClick={() => navigate(`/gigs/${gig.gig_id}/edit`)}
//   />

export function ListRow({
  primary,
  secondary,
  leading,
  trailing,
  onClick,
  style,
  ...rest
}) {
  const interactive = !!onClick;
  return (
    <div
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      style={{
        display: "flex",
        alignItems: "center",
        gap: space.lg,
        padding: `${space.md}px ${space.lg}px`,
        background: c.innerBg,
        border: border.inner,
        borderRadius: radius.lg,
        cursor: interactive ? "pointer" : "default",
        outline: "none",
        transition: `background ${motion.fast}`,
        ...style,
      }}
      {...rest}
    >
      {leading}
      <div style={{ flex: "1 1 0", minWidth: 0 }}>
        <div
          style={{
            ...t.listPrimary,
            color: c.text,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {primary}
        </div>
        {secondary && (
          <div
            style={{
              ...t.listSecondary,
              color: c.textDim,
              marginTop: 3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {secondary}
          </div>
        )}
      </div>
      {trailing && (
        <div
          style={{ display: "flex", alignItems: "center", gap: space.md, flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          {trailing}
        </div>
      )}
    </div>
  );
}
